import { ImgID } from "../../image_loader/img_id.js";
import { NetworkManager } from "../../network_manager.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";

export type ConnectionFailedVariant = "connection" | "loading_error" | "offline_levels_exhausted" | "offline_leaderboard";

const VARIANT_KEYS: Record<ConnectionFailedVariant, { title: string; text: string }> = {
  connection: { title: "CONNECTION.FAILED", text: "CONNECTION.MAIN_TEXT" },
  loading_error: { title: "LOADING_ERROR.TITLE", text: "LOADING_ERROR.TEXT" },
  offline_levels_exhausted: { title: "OFFLINE.LEVELS_EXHAUSTED.TITLE", text: "OFFLINE.LEVELS_EXHAUSTED.TEXT" },
  offline_leaderboard: { title: "OFFLINE.LEADERBOARD.TITLE", text: "OFFLINE.LEADERBOARD.TEXT" },
};

export class ConnectionFailedDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.CONNECTION_FAILED_DIALOG;

  private static readonly DESIGN_W = 906;
  private static readonly DESIGN_H = 990;
  private static readonly PANEL_Y = 40;
  private static readonly PANEL_H = 950;
  private static readonly ICON_Y = 90;
  private static readonly ICON_SIZE = 245;
  private static readonly TITLE_Y = 345;
  private static readonly MESSAGE_Y = 440;
  private static readonly BUTTON_Y = 630;
  private static readonly CLOSE_Y = 10;
  private static readonly BUTTON_H = 280;

  constructor( private readonly variant: ConnectionFailedVariant = "connection" )
  {
    const body_w = LayoutScaler.widthFromDesign( ConnectionFailedDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( ConnectionFailedDialog.DESIGN_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, false );

    this.createBackground();
    this.createStatusIcon();
    this.createTitle();
    this.createMessage();
    this.createReconnectButton();
    this.createCloseButton();
  }

  private createBackground(): void
  {
    this.addChild( new UINineSliceElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( ConnectionFailedDialog.PANEL_Y ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( ConnectionFailedDialog.PANEL_H ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    ) );
  }

  private createStatusIcon(): void
  {
    const icon_size = LayoutScaler.widthFromDesign( ConnectionFailedDialog.ICON_SIZE );
    const icon_x = ( this.rect.getW() - icon_size ) / 2;
    const icon_y = LayoutScaler.yFromDesign( ConnectionFailedDialog.ICON_Y );
    this.addChild( new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      ImgID.UI_ConnectionFailedIcon
    ) );
  }

  private createTitle(): void
  {
    this.addChild( new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( ConnectionFailedDialog.TITLE_Y ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 92 ),
      {
        localization_key: VARIANT_KEYS[this.variant].title,
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.menu_top_el_text,
        shadow_blur: LayoutScaler.widthFromDesign( 5 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    ) );
  }

  private createMessage(): void
  {
    const text_w = LayoutScaler.widthFromDesign( 770 );
    this.addChild( new UITextElement(
      this.ctx,
      ( this.rect.getW() - text_w ) / 2,
      LayoutScaler.yFromDesign( ConnectionFailedDialog.MESSAGE_Y ),
      text_w,
      LayoutScaler.heightFromDesign( 175 ),
      {
        localization_key: VARIANT_KEYS[this.variant].text,
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        auto_wrap: true,
        line_height: 1.0,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.menu_top_el_text,
      }
    ) );
  }

  private createReconnectButton(): void
  {
    const button_w = LayoutScaler.widthFromDesign( 700 );
    const button_h = LayoutScaler.heightFromDesign( ConnectionFailedDialog.BUTTON_H );
    const button_x = ( this.rect.getW() - button_w ) / 2;
    const button_y = LayoutScaler.yFromDesign( ConnectionFailedDialog.BUTTON_Y );
    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    const label_w = LayoutScaler.widthFromDesign( 560 );
    const label_x = ( button_w - label_w ) / 2;
    const label_y = LayoutScaler.yFromDesign( 5 );
    button.addChild( new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      button_h,
      {
        localization_key: "RECONNECT",
        font_size: LayoutScaler.textSizeFromDesign( 48 ),
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        auto_wrap: false,
        scale_to_width: true,
        outline_color: ColorScheme.menu_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        ...TextStyleScheme.buttonShadow(),
      }
    ), true );

    button.setOnClick( () =>
    {
      void NetworkManager.retryByUser();
    } );

    this.addChild( button );
  }

  private createCloseButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 114 );
    const close_x = this.rect.getW() - size + LayoutScaler.xFromDesign( 10 );
    const close_y = LayoutScaler.yFromDesign( ConnectionFailedDialog.CLOSE_Y );
    const close_button = new UIImage(
      this.ctx,
      close_x,
      close_y,
      size,
      size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    close_button.setOnClick( () => DialogManager.closeDialog() );
    this.addChild( close_button );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override onDialogWillClose(): void
  {
    NetworkManager.onDialogClosed();
  }

  public name(): string { return "ConnectionFailedDialog"; }
}
